import { useEffect, useState } from "react";
import { CheckCircle2, Circle, X, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CompanyProfileDialog, DepartmentsDialog, EmployeesDialog, OrgLinksDialog } from "./SetupDialogs";

const STEP_META = [
  ["company_profile", "setup.stepCompanyProfile", "setup.blurbCompanyProfile"],
  ["departments", "setup.stepDepartments", "setup.blurbDepartments"],
  ["employees", "setup.stepEmployees", "setup.blurbEmployees"],
  ["org_links", "setup.stepOrgLinks", "setup.blurbOrgLinks"],
];

export const SetupChecklist = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [openStep, setOpenStep] = useState(null);

  const load = () => api.get("/setup/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (user?.role !== "admin" || !status || status.dismissed || status.completed) return null;

  const done = Object.values(status.steps).filter(Boolean).length;

  const dismiss = async () => {
    await api.patch("/setup/dismiss");
    setStatus((s) => ({ ...s, dismissed: true }));
  };

  const toggleDemo = async () => {
    try {
      if (status.demo_loaded) {
        const { data } = await api.delete("/setup/demo-data");
        toast.success(t("setup.removedDemo", { count: data.removed }));
      } else {
        const { data } = await api.post("/setup/demo-data");
        toast.success(t("setup.loadedDemo", { count: data.loaded }));
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="bg-white border-2 mb-6" style={{ borderColor: "var(--brand-accent)" }} data-testid="setup-checklist">
      <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100">
        <div>
          <p className="text-[13px] font-bold" style={{ color: "var(--brand-primary)" }}>
            {t("setup.title", { done, total: STEP_META.length })}
          </p>
          <p className="text-[11px] text-slate-400 mt-0.5">{t("setup.subtitle")}</p>
        </div>
        <button data-testid="setup-dismiss-btn" onClick={dismiss} className="text-slate-300 hover:text-slate-500">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="grid md:grid-cols-4 divide-x divide-slate-100">
        {STEP_META.map(([key, labelKey, blurbKey]) => (
          <button key={key} data-testid={`setup-step-${key}`} onClick={() => setOpenStep(key)}
            className="px-4 py-3.5 text-start hover:bg-slate-50">
            <div className="flex items-center gap-2">
              {status.steps[key]
                ? <CheckCircle2 className="h-4 w-4" style={{ color: "var(--brand-accent)" }} />
                : <Circle className="h-4 w-4 text-slate-300" />}
              <span className="text-[12px] font-semibold text-slate-700">{t(labelKey)}</span>
            </div>
            <p className="text-[10px] text-slate-400 mt-1 ms-6">{t(blurbKey)}</p>
          </button>
        ))}
      </div>
      <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/60">
        <p className="text-[11px] text-slate-500">
          {t("setup.demoPrompt")}
        </p>
        <Button variant="outline" data-testid="setup-demo-toggle-btn" onClick={toggleDemo}
          className="rounded-none h-7 text-[11px]">
          {status.demo_loaded
            ? <><Trash2 className="h-3 w-3" /> {t("setup.removeDemo", { count: status.demo_count })}</>
            : <><Sparkles className="h-3 w-3" /> {t("setup.loadDemo")}</>}
        </Button>
      </div>

      <CompanyProfileDialog open={openStep === "company_profile"} onClose={() => { setOpenStep(null); load(); }} />
      <DepartmentsDialog open={openStep === "departments"} onClose={() => { setOpenStep(null); load(); }} />
      <EmployeesDialog open={openStep === "employees"} onClose={() => { setOpenStep(null); load(); }} />
      <OrgLinksDialog open={openStep === "org_links"} onClose={() => { setOpenStep(null); load(); }} />
    </div>
  );
};

export default SetupChecklist;
